<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">
            Billetage du {{ $format.date(cashCount && cashCount.operation_date) }}
          </h1>
          <div v-if="cashCount" class="small text-muted">
            Caissier : <strong>{{ cashCount.user ? cashCount.user.name : '-' }}</strong>
          </div>
        </div>

        <div class="d-flex gap-2">
          <button type="button" class="btn btn-outline-secondary btn-sm btn-icon-label" title="Retour à la liste" @click="$router.push({ name: 'CashCounts' })">
            <i class="fa fa-arrow-left"></i>
            <span class="btn-label ms-1">Retour à la liste</span>
          </button>

          <button v-if="cashCount && cashCount.status === 'draft'" type="button" class="btn btn-outline-primary btn-sm btn-icon-label" title="Modifier"
            @click="openEditDialog">
            <i class="fa fa-pen"></i>
            <span class="btn-label ms-1">Modifier</span>
          </button>

          <button v-if="cashCount && cashCount.status === 'draft'" type="button" class="btn btn-outline-success btn-sm btn-icon-label" title="Valider"
            @click="validateCashCount">
            <i class="fa fa-check"></i>
            <span class="btn-label ms-1">Valider</span>
          </button>

          <AppLoadingButton v-if="cashCount && cashCount.status === 'draft'" type="button" class="btn btn-outline-danger btn-sm btn-icon-label" title="Supprimer"
            :loading="isDeleting" loading-label="Suppression..." @click="deleteCashCount">
            <i class="fa fa-trash"></i>
            <span class="btn-label ms-1">Supprimer</span>
          </AppLoadingButton>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="alert alert-success py-2">
        {{ successMessage }}
      </div>

      <div v-if="isLoading" class="text-center my-5">
        <i class="fa fa-spinner fa-spin fa-2x mb-2"></i>
        <div class="small text-muted">Chargement...</div>
      </div>

      <div v-else-if="!cashCount" class="alert alert-warning">
        Billetage introuvable.
      </div>

      <div v-else class="row g-3 align-items-start">
        <!-- Informations générales -->
        <div class="col-lg-4">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-0 pb-2 d-flex justify-content-between align-items-center">
              <h6 class="mb-0">Informations</h6>
              <span class="badge" :class="cashCount.status === 'validated' ? 'bg-success' : 'bg-secondary'">
                {{ cashCount.status === 'validated' ? 'Validé' : 'Brouillon' }}
              </span>
            </div>
            <div class="card-body py-2">
              <div class="mb-2">
                <div class="small text-muted">Date du billetage</div>
                <div class="fw-semibold">
                  {{ $format.date(cashCount.operation_date) }}
                </div>
              </div>

              <div class="mb-2">
                <div class="small text-muted">Caissier</div>
                <div>{{ cashCount.user ? cashCount.user.name : '-' }}</div>
              </div>

              <div class="mb-2">
                <div class="small text-muted">Montant d'ouverture</div>
                <div>{{ $format.currency(cashCount.opening_amount) }}</div>
              </div>

              <div class="mb-2">
                <div class="small text-muted">Montant théorique</div>
                <div>{{ $format.currency(cashCount.expected_amount) }}</div>
              </div>

              <div class="mb-2">
                <div class="small text-muted">Montant compté</div>
                <div class="fw-semibold">{{ $format.currency(cashCount.counted_amount) }}</div>
              </div>

              <div class="mb-2">
                <div class="small text-muted">Différence</div>
                <div :class="{
                  'text-success fw-semibold': (cashCount.difference || 0) > 0,
                  'text-danger fw-semibold': (cashCount.difference || 0) < 0,
                  'text-muted': (cashCount.difference || 0) === 0,
                }">
                  {{ $format.currency(cashCount.difference) }}
                </div>
              </div>

              <div class="mb-2">
                <div class="small text-muted">Début du service</div>
                <div>{{ $format.datetime(cashCount.start_at) || '-' }}</div>
              </div>

              <div class="mb-2">
                <div class="small text-muted">Fin du service</div>
                <div>{{ $format.datetime(cashCount.end_at) || '-' }}</div>
              </div>

              <div v-if="cashCount.validator" class="mb-2">
                <div class="small text-muted">Validé par</div>
                <div>
                  {{ cashCount.validator.name }}
                  <span v-if="cashCount.validated_at" class="small text-muted">
                    ({{ $format.datetime(cashCount.validated_at) }})
                  </span>
                </div>
              </div>

              <div v-if="cashCount.comment" class="mt-3">
                <div class="small text-muted">Commentaire</div>
                <div>{{ cashCount.comment }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Détail billets / pièces -->
        <div class="col-lg-8">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-0 pb-2 d-flex justify-content-between align-items-center">
              <h6 class="mb-0">Détail des billets et pièces</h6>
              <small class="text-muted">
                {{ displayedCashCountLines.length }} ligne(s)
              </small>
            </div>
            <div class="card-body py-2">
              <div class="table-responsive">
                <table class="table table-sm table-striped table-bordered align-middle mb-0">
                  <thead class="table-light">
                    <tr>
                      <th>Libellé</th>
                      <th class="text-end">Valeur faciale</th>
                      <th class="text-end">Quantité</th>
                      <th class="text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(line, index) in displayedCashCountLines" :key="index">
                      <td>{{ line.label }}</td>
                      <td class="text-end">{{ $format.currency(line.denomination) }}</td>
                      <td class="text-end">{{ line.quantity }}</td>
                      <td class="text-end">{{ $format.currency(line.line_total) }}</td>
                    </tr>
                    <tr v-if="displayedCashCountLines.length === 0">
                      <td colspan="4" class="text-center text-muted small">
                        Aucune ligne de billetage renseignée.
                      </td>
                    </tr>
                  </tbody>
                  <tfoot v-if="displayedCashCountLines.length > 0">
                    <tr>
                      <th colspan="3" class="text-end">Total compté</th>
                      <th class="text-end">{{ $format.currency(cashCount.counted_amount) }}</th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Dialog édition -->
      <PrimevueDialog v-model:visible="formDialogVisible" :modal="true" :style="{ width: '600px' }" :closable="false">
        <template #header>
          <h5 class="mb-0">Modifier le pointage</h5>
        </template>

        <CashCountForm v-if="cashCount" :mode="'edit'" :cashCount="cashCount" @saved="handleSaved"
          @cancel="closeFormDialog" />
      </PrimevueDialog>
    </template>
  </AdminLayout>
</template>

<script>
import CashCountForm from './CashCountForm.vue';

export default {
  name: 'CashCountShowView',
  components: {
    CashCountForm,
  },
  data() {
    return {
      cashCount: null,
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      formDialogVisible: false,
      isDeleting: false,
    };
  },
  computed: {
    displayedCashCountLines() {
      return Array.isArray(this.cashCount?.lines)
        ? this.cashCount.lines.filter((line) => Number(line.quantity || 0) > 0)
        : [];
    },
  },
  mounted() {
    this.fetchCashCount();
  },
  methods: {
    async fetchCashCount() {
      const id = this.$route.params.id;
      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';
      this.cashCount = null;

      try {
        const res = await this.$api.get(`/cash-counts/${id}`);
        this.cashCount = res.data.data || res.data;
      } catch (error) {
        console.error('Erreur chargement billetage', error);
        const errors = error.response?.data?.errors;
        this.errorMessage = errors
          ? Object.values(errors).flat().join(' ')
          : 'Impossible de charger ce billetage.';
      } finally {
        this.isLoading = false;
      }
    },

    openEditDialog() {
      if (!this.cashCount) return;
      this.formDialogVisible = true;
    },

    closeFormDialog() {
      this.formDialogVisible = false;
    },

    async handleSaved() {
      this.formDialogVisible = false;
      this.successMessage = 'Billetage mis à jour avec succès.';
      await this.fetchCashCount();
    },

    async validateCashCount() {
      if (!this.cashCount) return;

      const ok = await this.$confirmAction(
        'Valider ce billetage ? Cette action indique la fin de votre service pour cette journée.'
      );
      if (!ok) return;

      this.errorMessage = '';
      this.successMessage = '';

      try {
        await this.$api.post(`/cash-counts/${this.cashCount.id}/validate`);
        this.successMessage = 'Billetage validé avec succès.';
        await this.fetchCashCount();
      } catch (error) {
        console.error('Erreur validation billetage', error);
        const errors = error.response?.data?.errors;
        this.errorMessage = errors
          ? Object.values(errors).flat().join(' ')
          : 'Une erreur est survenue lors de la validation.';
      }
    },

    async deleteCashCount() {
      if (!this.cashCount) return;
      if (this.isDeleting) return;

      this.isDeleting = true;

      const ok = await this.$confirmAction('Supprimer ce billetage (non validé) ?');
      if (!ok) {
        this.isDeleting = false;
        return;
      }

      this.errorMessage = '';
      this.successMessage = '';

      try {
        await this.$api.delete(`/cash-counts/${this.cashCount.id}`);
        this.successMessage = 'Billetage supprimé avec succès.';
        this.$router.push({ name: 'CashCounts' });
      } catch (error) {
        console.error('Erreur suppression billetage', error);
        const errors = error.response?.data?.errors;
        this.errorMessage = errors
          ? Object.values(errors).flat().join(' ')
          : 'Une erreur est survenue lors de la suppression.';
      } finally {
        this.isDeleting = false;
      }
    },
  },
};
</script>

<style scoped>
.card {
  border-radius: 0.75rem;
}
</style>
